import { useAtom, useSetAtom } from 'jotai';
import React, { useState } from 'react';
import { useRightPanel } from '../../app/layout/RightPanel';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button, { IconButton } from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal';
import FloatingCard from '../../components/FloatingCard';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import Popover from '../../components/Popover';
import SVGIcon from '../../components/SVGIcon';
import StickyHeader from '../../components/StickyHeader';
import Tooltip from '../../components/Tooltip';
import {
  BEACON_POST_OVERFLOW_MENU,
  BEACON_POST_VIEW_HISTORY,
} from '../../constants/onboarding';
import { EditorActions } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { PostType, UserType } from '../../graphql/generated/graphql';
import archivePostIcon from '../../icons/non-prefilled/16x16/archive.svg';
import closeIcon from '../../icons/non-prefilled/16x16/clear.svg';
import columnsIcon from '../../icons/non-prefilled/16x16/columns.svg';
import copyIcon from '../../icons/non-prefilled/16x16/copy.svg';
import archiveDraftIcon from '../../icons/non-prefilled/16x16/draftArchived.svg';
import editTopicsIcon from '../../icons/non-prefilled/16x16/editTopics.svg';
import historyIcon from '../../icons/non-prefilled/16x16/history.svg';
import imageIcon from '../../icons/non-prefilled/16x16/image.svg';
import informationIcon from '../../icons/non-prefilled/16x16/information.svg';
import memberIcon from '../../icons/non-prefilled/16x16/member.svg';
import newTabIcon from '../../icons/non-prefilled/16x16/newTab.svg';
import publishIcon from '../../icons/non-prefilled/16x16/publish.svg';
import seriesIcon from '../../icons/non-prefilled/16x16/series.svg';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import unarchiveIcon from '../../icons/non-prefilled/16x16/unarchive.svg';
import unpublishIcon from '../../icons/non-prefilled/16x16/unpublish.svg';
import TutorialBeacon from '../../onboarding/tutorial/TutorialBeacon';
import AddSeriesModal from '../../series/modal/AddSeriesModal';
import TemplatesMenuItem from '../../templates/TemplatesMenuItem';
import { postToasts } from '../../toasts/toasts';
import AiCopilotMenuItem from '../../topbar/AiCopilotMenuItem';
import ImportMenuButton from '../../topbar/ImportMenuButton';
import { useAddRecentlyUsedTemplateId } from '../../ui/atoms';
import { UIActions } from '../../ui/uiSlice';
import download from '../../utils/network/download';
import useMainContentHistory from '../../utils/router/useMainContentHistory';
import postPath from '../../utils/url/postPath';
import CreatePostMenuItem from '../CreatePostMenuItem';
import PostInsights from '../PostInsights';
import VerifyMenuItems, {
  useShouldRenderVerifyMenuItems,
} from '../VerifyMenuItems';
import { isAddOrShareMenuOpenAtom } from '../controlBar/controlBarMenuAtoms';
import { PostPolicyAction, usePostPolicyCheck } from '../postPolicyCheck';
import SharingDropdown from '../sharing/SharingDropdown';
import { usePostPermissionSubscription } from '../sharing/postPermissionOperations';
import CopyEmbedMenuItem from '../topbar/CopyEmbedMenuItem';
import SidebarMenuItem from '../topbar/SidebarMenuItem';
import TemplatizeMenuItem from '../topbar/TemplatizeMenuItem';
import useAddCover from '../useAddCover';
import useCanDisplayPostHistoryView from '../useCanDisplayPostHistoryView';
import useDeletePost from '../useDeletePost';
import useDuplicatePost from '../useDuplicatePost';
import usePublishPost from '../usePublishPost';
import useToggleArchived from '../useToggleArchived';
import useIsLayoutSize from '../utils/useIsLayoutSize';
import { LayoutSize } from '../utils/useLayoutSize';
import * as styles from './PostEditButton.module.scss';
import { isShareDropdownOpenAtom } from './isShareDropdownOpenAtom';

const POST_EDIT_BUTTON_POST_FRAGMENT = gql(`
  fragment PostEditButton_post on Post {
    id
    publishedAt
    ...usePostEditButtonMenu_post
  }
`);

const USE_POST_EDIT_BUTTON_MENU_POST_FRAGMENT = gql(`
  fragment usePostEditButtonMenu_post on Post {
      id
      publishedAt
      archivedAt
      editedAt
      insertedAt
      externalPost {
        id
      }
      banner {
        original
      }
      topics {
        id
      }
      series {
        id
      }
      ...usePostPolicyCheck_post
      ...VerifyMenuItems_post
      ...postPath_post
      ...CopyEmbedMenuItem_post
      ...TemplatizeMenuItem_post
  }
`);

type UsePostEditButtonMenuOption = {
  post: DocumentType<typeof USE_POST_EDIT_BUTTON_MENU_POST_FRAGMENT>;
  onOpenShareDropdown?: () => void;
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const usePostEditButtonMenu = ({
  post,
  onOpenShareDropdown,
}: UsePostEditButtonMenuOption) => {
  const dispatch = useAppDispatch();
  const mainContentHistory = useMainContentHistory();

  const postTopicIds = post.topics.map(({ id }) => id);

  const isMobile = useIsLayoutSize(LayoutSize.mobile);
  const currentUser = useAppSelector((state) => state.session.user);
  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const isEditable = useAppSelector((state) => state.editor.editable);

  const publishPost = usePublishPost(post.id);
  const toggleArchived = useToggleArchived();

  const deletePost = useDeletePost();
  const duplicatePost = useDuplicatePost();

  const postPolicyCheck = usePostPolicyCheck();
  const shouldRenderVerifyMenuItems = useShouldRenderVerifyMenuItems();
  const setIsAddOrShareMenuOpen = useSetAtom(isAddOrShareMenuOpenAtom);
  const onAddCover = useAddCover();
  const addRecentlyUsedTemplateId = useAddRecentlyUsedTemplateId();

  const { id, title, publishedAt, externalPost, archivedAt, banner } = post;

  const onToggleArchive = () => {
    toggleArchived(post);
  };

  const onDuplicate = async () => {
    await duplicatePost(id);

    if (post.type === PostType.Template) {
      addRecentlyUsedTemplateId(post.id);
    }
  };

  const onExport = (type: string, extension: string) => {
    download(
      `/posts/${id}/export/${type}`,
      `${title} - Slab.${extension}`,
    ).catch(() => {
      dispatch(UIActions.pushToast(postToasts.postExportError));
    });
  };

  const handleDelete = () => {
    dispatch(
      UIActions.openModal(ConfirmationModal, {
        title: 'Delete this post?',
        description: 'Are you sure you want to delete this post?',
        confirmButton: {
          text: 'Delete',
          onClick: async () => {
            await deletePost(id);
            mainContentHistory.back(
              ({ pathname }, index) => {
                const isPreviousLocation = index === 1;
                return (
                  isPreviousLocation &&
                  !pathname.includes(post.id) &&
                  pathname !== '/'
                );
              },
              { fallback: publishedAt ? '/' : '/drafts' },
            );
          },
        },
        width: 447,
      }),
    );
  };

  const canEditPost = postPolicyCheck(
    PostPolicyAction.EditPost,
    post,
    currentUser,
  );

  const isPostPublishDisabled =
    currentUser.type === UserType.Guest && postTopicIds.length === 0;

  const archiveIcon = publishedAt ? archivePostIcon : archiveDraftIcon;

  const lifeCycleMenuItems = [
    shouldRenderVerifyMenuItems({ post, user: currentUser }) && (
      <VerifyMenuItems key="verify" post={post} />
    ),
    canEditPost && !externalPost && !archivedAt && (
      <React.Fragment key="publish">
        {publishedAt ? (
          <MenuItem
            key="unpublish"
            icon={unpublishIcon}
            onClick={() => publishPost(false)}
          >
            Unpublish
          </MenuItem>
        ) : (
          <MenuItem
            key="publish"
            icon={publishIcon}
            onClick={() => publishPost(true)}
            disabled={isPostPublishDisabled}
            tooltip={
              isPostPublishDisabled
                ? 'Guests must publish\nposts in at least\none topic'
                : undefined
            }
          >
            Publish
          </MenuItem>
        )}
      </React.Fragment>
    ),
    <SidebarMenuItem key="sidebarItem" item={post} />,
    canEditPost && (
      <MenuItem
        key="archive"
        icon={archivedAt ? unarchiveIcon : archiveIcon}
        onClick={onToggleArchive}
      >
        {archivedAt ? 'Unarchive' : 'Archive'}
      </MenuItem>
    ),
  ].filter(Boolean);

  const { openRightPanel } = useRightPanel();

  const canDisplayPostHistoryView = useCanDisplayPostHistoryView();

  return (
    <>
      <CreatePostMenuItem />
      <TemplatesMenuItem />
      {!isMobile && <ImportMenuButton />}
      <AiCopilotMenuItem />
      <MenuSeparator />
      {!externalPost && canDisplayPostHistoryView && (
        <TutorialBeacon id={BEACON_POST_VIEW_HISTORY} placement="left">
          <MenuItem
            icon={historyIcon}
            to={postPath(post, {
              histories: 'last',
            })}
            disabled={post.editedAt <= post.insertedAt}
          >
            View history
          </MenuItem>
        </TutorialBeacon>
      )}
      {!isMobile && (
        <MenuItem
          icon={columnsIcon}
          onClick={() => {
            openRightPanel(<PostInsights id={id} />);
          }}
        >
          View insights
        </MenuItem>
      )}
      {onOpenShareDropdown && (
        <MenuItem onClick={onOpenShareDropdown} icon={memberIcon}>
          Share post
        </MenuItem>
      )}
      {!isMobile && (
        <>
          {banner?.original != null ? (
            <MenuItem
              icon={imageIcon}
              onClick={() => {
                dispatch(EditorActions.setIsPostBannerPopOverOpen(true));
              }}
              disabled={!canEditPost}
            >
              Edit cover
            </MenuItem>
          ) : (
            <MenuItem
              icon={imageIcon}
              onClick={onAddCover}
              disabled={!canEditPost}
            >
              Add cover
            </MenuItem>
          )}
        </>
      )}

      {(isEditable || (canEditPost && !!externalPost)) && (
        <MenuItem
          icon={editTopicsIcon}
          onClick={() => {
            if (readOnly) {
              dispatch(EditorActions.readOnly(false));
            }
            // Timeout is needed when the editor changes to editing state to focus the topic input.
            setTimeout(() => {
              setIsAddOrShareMenuOpen(true);
            }, 1);
          }}
        >
          Edit topics
        </MenuItem>
      )}
      {!post.series && !!publishedAt && canEditPost && !archivedAt && (
        <MenuItem
          icon={seriesIcon}
          onClick={() =>
            dispatch(UIActions.openModal(AddSeriesModal, { post }))
          }
        >
          Add to series
        </MenuItem>
      )}
      {!archivedAt && !externalPost && <TemplatizeMenuItem post={post} />}
      <MenuItem icon={copyIcon} onClick={onDuplicate}>
        {post.type === PostType.Template ? 'Use template' : 'Make a copy'}
      </MenuItem>
      {!isMobile && <CopyEmbedMenuItem post={post} />}
      {!isMobile && (
        <MenuItem
          icon={newTabIcon}
          submenuItems={
            <>
              <MenuItem onClick={() => onExport('docx', 'docx')}>Docx</MenuItem>
              <MenuItem onClick={() => onExport('markdown', 'md')}>
                Markdown
              </MenuItem>
              <MenuItem
                onClick={() => {
                  window.print();
                }}
              >
                Print to PDF
                <span className={styles.tooltip}>
                  <Tooltip
                    content={
                      'Select PDF as the\nprinter destination\nto export as PDF'
                    }
                    placement="bottom"
                    display="inline"
                  >
                    <SVGIcon className={styles.icon} inline>
                      {informationIcon}
                    </SVGIcon>
                  </Tooltip>
                </span>
              </MenuItem>
            </>
          }
        >
          Export as
        </MenuItem>
      )}
      {lifeCycleMenuItems.length > 0 && (
        <>
          <MenuSeparator />
          {lifeCycleMenuItems}
        </>
      )}
      {!externalPost && canEditPost && (
        <>
          <MenuSeparator />
          <MenuItem danger icon={trashIcon} onClick={handleDelete}>
            Delete
          </MenuItem>
        </>
      )}
    </>
  );
};

interface PostEditButtonProps {
  post: DocumentType<typeof POST_EDIT_BUTTON_POST_FRAGMENT>;
  mobile?: boolean;
}

const PostEditButton = ({ post, mobile }: PostEditButtonProps) => {
  const [isEditMenuOpen, setIsEditMenuOpen] = useState(false);
  const [isShareDropdownOpen, setIsShareDropdownOpen] = useAtom(
    isShareDropdownOpenAtom,
  );

  usePostPermissionSubscription(post.id);

  const menu = usePostEditButtonMenu({
    post,
    onOpenShareDropdown: mobile
      ? () => setIsShareDropdownOpen({ open: true, autoFocus: false })
      : undefined,
  });

  return (
    <div className={styles.container}>
      {mobile ? (
        isShareDropdownOpen.open && (
          <div className={styles.fullScreenShareDropdown}>
            <div
              className={styles.shareDropdownBackdrop}
              onClick={() => {
                setIsShareDropdownOpen({ open: false, autoFocus: false });
              }}
            />
            <div className={styles.shareDropdownContainer}>
              <StickyHeader>
                <div className={styles.shareDropdownTitle}>
                  <h3>Share</h3>
                  <IconButton
                    className={styles.shareDropdownClose}
                    onClick={() => {
                      setIsShareDropdownOpen({
                        open: false,
                        autoFocus: false,
                      });
                    }}
                    applyNegativeMargin
                    aria-label="Close"
                  >
                    {closeIcon}
                  </IconButton>
                </div>
              </StickyHeader>
              <SharingDropdown
                close={() => {
                  setIsShareDropdownOpen({ open: false, autoFocus: false });
                }}
                postId={post.id}
                autoFocus={isShareDropdownOpen.autoFocus}
                title="Share"
              />
            </div>
          </div>
        )
      ) : (
        <Popover
          open={isShareDropdownOpen.open}
          onOpenChange={(open) => {
            setIsShareDropdownOpen({ open, autoFocus: false });
          }}
          view={
            <FloatingCard noArrow>
              <SharingDropdown
                close={() => {
                  setIsShareDropdownOpen({ open: false, autoFocus: false });
                }}
                postId={post.id}
                autoFocus={isShareDropdownOpen.autoFocus}
              />
            </FloatingCard>
          }
          placement="bottom-end"
          portalContainer="disabled"
        >
          <Button variant="secondary" className={styles.shareButton}>
            Share
          </Button>
        </Popover>
      )}
      <div className={styles.overflowMenu}>
        <OverflowMenu
          beaconId={BEACON_POST_OVERFLOW_MENU}
          beaconPlacement="bottom"
          open={isEditMenuOpen}
          onOpenChange={setIsEditMenuOpen}
          ariaLabel="Post options"
          portalContainer="disabled"
        >
          {menu}
        </OverflowMenu>
      </div>
    </div>
  );
};

export default PostEditButton;
